// The MOODS phone apps and the web build they bundle (docs/DECISIONS.md › iOS uygulaması, Android uygulaması —
// Capacitor). The iOS app (apps/ios) serves the production web build on `app://moods` like the desktop shell; the
// Android app (apps/android) on `https://moods` (Android's web view only treats http(s) as a secure context with a
// working history API). Before the page runs the shell injects `window.moodsNative`; native calls go to its
// `MoodsNative` plugin (apps/ios/ios/App/App/MoodsNativePlugin.swift, apps/android/android/app/src/main/java/gg/moods/app/
// MoodsNativePlugin.kt — same methods, same events) through Capacitor's injected bridge (`window.Capacitor`), so the web
// app ships no Capacitor package. The web app only talks to it through apps/web/src/lib/native-bridge.ts and
// lib/native.ts. Types only (plus constants).
import { DESKTOP_APP_ORIGIN } from './desktop.ts';

/** Origin of the bundled web app inside the Android app (Capacitor `androidScheme: https`, `hostname: moods`). */
export const ANDROID_APP_ORIGIN = 'https://moods';

/** The app origins the API, the file store and the cinema player allow next to APP_URL (desktop + iOS, Android). */
export const APP_SHELL_ORIGINS = [DESKTOP_APP_ORIGIN, ANDROID_APP_ORIGIN] as const;

export type NativePlatform = 'ios' | 'android';

/** What the shell hands to the web app before it runs (a script at document start). */
export interface MoodsNativeShell {
  platform: NativePlatform;
  /** iOS: CFBundleShortVersionString · CFBundleVersion; Android: versionName · versionCode */
  version: string;
  build: string;
  /** endpoints of the build (iOS: Debug = the local stack, Release = moods.gg — apps/ios/ios/App/*.xcconfig; Android: the build type) */
  config: { apiUrl: string; gatewayUrl: string; siteUrl: string };
  /** what this build does natively (absent in iOS builds before 1.1) */
  features?: NativeFeature[];
}

/**
 * `voice`: the native voice engine (MoodsVoiceEngine.swift / .kt, 1.1) carries the call's audio; the web view is its
 * companion. `push`: the build can register for push (Android: a Firebase configuration was built in).
 */
export type NativeFeature = 'voice' | 'push';

/** `voiceState` event of the native voice engine; `reason` names an unexpected disconnect. */
export interface NativeVoiceStateEvent {
  state: 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
  reason: 'removed' | 'duplicate' | 'roomDeleted' | 'network' | null;
}

/** jsName of the app's own Capacitor plugin. */
export const NATIVE_PLUGIN = 'MoodsNative';

/** Secure-storage items of the plugin (the Keychain, the Android Keystore) — the two tokens the desktop keeps with safeStorage. */
export type NativeSecret = 'refreshToken' | 'deviceToken';

/** Status bar text colour: `light` on dark themes, `dark` on the light theme. */
export type NativeStatusBarStyle = 'light' | 'dark';

/**
 * Plugin events: `deepLink` = a moods:// link or an https://app.moods.gg universal link / App Link opened the app;
 * `pushOpened` = a tapped notification, `url` being its in-app path. Both are kept until the web app listens (cold
 * starts). Android only: `voiceLeave` = "Ayrıl" on the ongoing call notification (the web app leaves the call).
 */
export interface NativeDeepLinkEvent {
  url: string;
}

/** `pushStatus`: notification permission of the app (`prompt` = never asked). */
export type NativePushStatus = 'granted' | 'denied' | 'prompt';

/**
 * `pushRegister` (asks when never asked, then fetches the device token). iOS: the APNs token (sandbox = a debug
 * build); Android: the FCM registration token (`sandbox` false).
 */
export type NativePushRegistration = { status: 'granted'; token: string; sandbox: boolean } | { status: 'denied' };

/** The two calls of Capacitor's injected JS bridge (native-bridge.js) the web app uses. */
export interface CapacitorBridgeGlobal {
  nativePromise<T = unknown>(plugin: string, method: string, options?: object): Promise<T>;
  addListener(plugin: string, event: string, callback: (data: unknown) => void): { remove: () => Promise<void> };
}
